import React, { useCallback, useMemo, type JSX } from 'react'
import { Heading, Paragraph } from '@alto-avios/alto-ui'
import { Modal } from '@reward-platform/lift/components'
import {
  CarHireLocation,
  getCarHireBrandName,
  getBrandedAddresses,
  getBrandedDetails,
  getMapPins,
  getReportableDeskSeparationMetres,
  isSamePhoneNumber,
} from '@reward-platform/ancillaries-locations'
import { useFormatter, useTranslations } from 'next-intl'
import useImperialUnits from '~/hooks/useImperialUnits/useImperialUnits'
import { useUser } from '~/context/user'
import StaticMap from '../StaticMap'
import styles from './LocationDetailsModal.module.scss'

const METRES_PER_MILE = 1609.344

/** Half a mile, past which a walk reads better in miles than in four figures of yards or feet. */
const HALF_MILE_METRES = METRES_PER_MILE / 2

/**
 * The two imperial small units, each with the step it is spoken in: a straight line between two
 * published desk points is worth about that much and no more.
 */
const IMPERIAL_SMALL_UNITS = {
  foot: { metresPer: 0.3048, roundToNearest: 50 },
  yard: { metresPer: 0.9144, roundToNearest: 10 },
} as const

/**
 * Only the US quotes a short walk in feet. Every other country that reaches the imperial branch -
 * today only GB, but the unit rule is the opCo's to change - is spoken to in yards rather than in
 * a unit it does not use.
 */
const smallImperialUnitFor = (userCountry: string | undefined) =>
  userCountry === 'US' ? 'foot' : 'yard'

const WEEKDAY_TO_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

const CLOSED_RANGE = /^\s*00:00\s*[—–-]\s*00:00\s*$/
const OPEN_24_HOURS_RANGE = /^\s*00:00\s*[—–-]\s*23:59\s*$/

export type LocationDetailsModalProps = {
  location: CarHireLocation
  isOpen: boolean
  onClose: () => void
}
function LocationDetailsModal({ location, isOpen, onClose }: LocationDetailsModalProps) {
  const t = useTranslations('pages.search.car_hire.location_details_modal.labels')
  const format = useFormatter()
  const shouldUseImperialUnits = useImperialUnits()
  const { userCountry } = useUser()

  const formatWeekday = useCallback(
    (day: string): string => {
      const dayIndex = WEEKDAY_TO_INDEX[day.trim().toLowerCase()]
      if (dayIndex === undefined) {
        return day
      }
      const referenceDate = new Date(Date.UTC(2020, 10, 22 + dayIndex))
      const localizedDay = format.dateTime(referenceDate, { weekday: 'long', timeZone: 'UTC' })
      return `${localizedDay.charAt(0).toLocaleUpperCase()}${localizedDay.slice(1)}`
    },
    [format]
  )

  const telephoneNumbers = useMemo(
    () =>
      getBrandedDetails(
        location,
        (vendor) => vendor.telephoneNumber,
        location.telephoneNumber,
        isSamePhoneNumber
      ),
    [location]
  )

  const addresses = useMemo(() => getBrandedAddresses(location), [location])

  const pins = useMemo(() => getMapPins(location), [location])

  // The same call the map made, so the sentence never describes a second desk the map did not draw.
  //
  // Metric or imperial is the app's own rule, the one that already decides whether a room is sized
  // in square feet, so a single journey never mixes the two systems. Which imperial unit a short
  // walk is quoted in is a further question, and only the member's country answers it.
  const deskSeparation = useMemo(() => {
    const metres = getReportableDeskSeparationMetres(location)
    if (metres === undefined) {
      return undefined
    }
    if (shouldUseImperialUnits) {
      if (metres >= HALF_MILE_METRES) {
        return format.number(metres / METRES_PER_MILE, {
          style: 'unit',
          unit: 'mile',
          maximumFractionDigits: 1,
        })
      }
      const unit = smallImperialUnitFor(userCountry)
      const { metresPer, roundToNearest } = IMPERIAL_SMALL_UNITS[unit]
      return format.number(Math.round(metres / metresPer / roundToNearest) * roundToNearest, {
        style: 'unit',
        unit,
      })
    }
    return metres >= 1000
      ? format.number(metres / 1000, {
          style: 'unit',
          unit: 'kilometer',
          maximumFractionDigits: 1,
        })
      : format.number(Math.round(metres / 10) * 10, { style: 'unit', unit: 'meter' })
  }, [format, location, shouldUseImperialUnits, userCountry])

  // A pin carries the brand's initial, so the heading names the letter the customer is looking for.
  const deskLabel = useCallback(
    (brandName: string): string => {
      const pin = pins.find((candidate) => candidate.brandName === brandName)?.label
      return pin ? t('desk_with_pin', { brandName, pin }) : t('desk', { brandName })
    },
    [pins, t]
  )

  const formatOpeningHours = useCallback(
    (hours: string): string => (OPEN_24_HOURS_RANGE.test(hours) ? t('open_24_hours') : hours),
    [t]
  )

  const openingHoursCells = useCallback(
    (hours: string[]): JSX.Element | JSX.Element[] => {
      const openingRanges = hours.filter((range) => !CLOSED_RANGE.test(range))
      if (!openingRanges.length) {
        return <td>{t('closed')}</td>
      }
      return openingRanges.map((range) => <td key={range}>{formatOpeningHours(range)}</td>)
    },
    [formatOpeningHours, t]
  )

  const locationOpeningTimesTable = useMemo(() => {
    const vendorsWithOpeningTimes = (location.vendors ?? []).filter(
      (vendor) => vendor.openingTimes?.length
    )
    const hasDifferentBrandSchedules =
      new Set(vendorsWithOpeningTimes.map((vendor) => JSON.stringify(vendor.openingTimes))).size > 1

    if (hasDifferentBrandSchedules) {
      return vendorsWithOpeningTimes.map((vendor) => {
        const brandName = getCarHireBrandName(vendor.brandId) ?? vendor.brandId
        return (
          <div key={vendor.brandId}>
            <Heading as="h4">{deskLabel(brandName)}</Heading>
            <table>
              <tbody>
                {vendor.openingTimes?.map(
                  (row): JSX.Element => (
                    <tr key={row.day}>
                      <th scope="row">{formatWeekday(row.day)}</th>
                      {openingHoursCells(row.hours)}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )
      })
    }

    if (location.openingTimes) {
      const rows = location.openingTimes.map((row): JSX.Element => {
        return (
          <tr key={row.day}>
            <th scope="row">{formatWeekday(row.day)}</th>
            {openingHoursCells(row.hours)}
          </tr>
        )
      })
      return (
        <table>
          <tbody>{rows}</tbody>
        </table>
      )
    }
    return null
  }, [deskLabel, formatWeekday, location.openingTimes, location.vendors, openingHoursCells])

  return (
    <Modal onClose={onClose} isOpen={isOpen} size="xl">
      <div className={styles.locationDetails}>
        <div className={styles.locationContact}>
          <Heading as="h2">{location.name}</Heading>
          {telephoneNumbers.map((telephoneNumber) => (
            <Paragraph key={`${telephoneNumber.brandName ?? ''}-${telephoneNumber.value}`}>
              {telephoneNumber.brandName
                ? `${t('call_brand', { brandName: telephoneNumber.brandName })}: ${
                    telephoneNumber.value
                  }`
                : `${t('call')}: ${telephoneNumber.value}`}
            </Paragraph>
          ))}
          <Heading as="h3">{t('location')}</Heading>
          {addresses.map((addressDetail) => (
            <address key={`${addressDetail.brandName ?? ''}-${addressDetail.value.join()}`}>
              {addressDetail.brandName && (
                <span>
                  <strong>{deskLabel(addressDetail.brandName)}</strong> <br />
                </span>
              )}
              {addressDetail.value.map((loc: string) => (
                <span key={loc}>
                  {loc} <br />
                </span>
              ))}
            </address>
          ))}
          {deskSeparation && (
            <Paragraph>{t('desks_apart', { distance: deskSeparation })}</Paragraph>
          )}
          <div
            className={`${styles.mapContainer} ${
              pins.length > 1 ? styles.mapContainerTwoDesks : ''
            }`}
          >
            {pins.length > 0 && <StaticMap pins={pins} zoom={13} scale={5} format="png" />}
          </div>
        </div>
        <div>
          <Heading as="h3">{t('opening_hours')}</Heading>
          {locationOpeningTimesTable}
          {location.afterHoursIndicator && (
            <>
              <Heading as="h4">{t('drop_off_24_hour')}</Heading>
              <Paragraph>{t('drop_off_message')}</Paragraph>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default LocationDetailsModal
